"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnknownError = exports.CodedError = void 0;
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
const stackTraceParser = __importStar(require("stacktrace-parser"));
const util_1 = __importDefault(require("util"));
const node_url_1 = require("node:url");
const node_path_1 = __importDefault(require("node:path"));
/**
 * Base class for all coded errors
 *
 * Coded errors are errors that have a code and optionally a details object
 * that can be used to debug the error.
 *
 * The code also brands the error type, allowing typescript to narrow the type
 * of the error based on the code and achieve nominal typing.
 *
 * @see [Nominal Typing Vs Structural Typing]({@link https://www.typescriptlang.org/docs/handbook/type-compatibility.html})
 *
 * @template Code - The type of the code of the error
 * @template Details - The type of the details object of the error
 */
class CodedError extends Error {
    constructor(code, message, details) {
        super(message);
        this.code = code;
        this.name = code;
        this.details = details;
        this._contextStack = [];
        // Prevent accidental mutation after construction (shallow)
        Object.freeze(this.details);
    }
    safeStringify(value) {
        try {
            return JSON.stringify(value, null, 2);
        }
        catch (_a) {
            try {
                return util_1.default.inspect(value, { depth: 3, maxArrayLength: 50 });
            }
            catch (_b) {
                return '[Unserializable details]';
            }
        }
    }
    get contextStack() {
        return Object.freeze([...this._contextStack]);
    }
    /**
     * Adds context information to the error for a more informative stack trace.
     * @param info - Information about what might have caused the error
     * @returns The current error instance for chaining
     */
    addContext(info) {
        var _a;
        // Capture the current stack trace and exclude this method's frame
        const err = new Error();
        // In V8/Node, this excludes frames up to `addContext`
        Error.captureStackTrace(err, this.addContext);
        let parsedStack = [];
        try {
            parsedStack = stackTraceParser.parse((_a = err.stack) !== null && _a !== void 0 ? _a : '');
        }
        catch (_b) {
            // fall through; no context if parsing fails
        }
        // Use the top entry in the stack trace as the context
        if (parsedStack.length > 0) {
            const { file, methodName, lineNumber, column } = parsedStack[0];
            let fullFilePath = file;
            let relativePath = fullFilePath;
            try {
                // Normalize file:// URLs produced by some runtimes/bundlers
                if (fullFilePath === null || fullFilePath === void 0 ? void 0 : fullFilePath.startsWith('file://')) {
                    fullFilePath = (0, node_url_1.fileURLToPath)(fullFilePath);
                }
                if (fullFilePath && node_path_1.default.isAbsolute(fullFilePath)) {
                    relativePath = node_path_1.default.relative(process.cwd(), fullFilePath);
                }
            }
            catch (_c) {
                // best-effort; keep as-is
            }
            const context = {
                fileName: relativePath !== null && relativePath !== void 0 ? relativePath : 'unknown',
                functionName: methodName !== null && methodName !== void 0 ? methodName : '<anonymous>',
                lineNumber: lineNumber,
                columnNumber: column,
                info,
            };
            this._contextStack.push(context);
        }
        return this;
    }
    /**
     * Generates pretty-printed string representation of the error,
     * including error code, message, details, and context stack.
     */
    prettyPrint() {
        const outputLines = [];
        outputLines.push(`Error [${this.code}]: ${this.message}`);
        outputLines.push(`Details: ${this.safeStringify(this.details)}`);
        if (this._contextStack.length > 0) {
            outputLines.push('Context Stack:');
            const contextInfo = this._contextStack
                .map((ctx) => {
                let location = '';
                if (ctx.fileName) {
                    location = ctx.fileName;
                    if (ctx.lineNumber && ctx.columnNumber) {
                        location += `:${ctx.lineNumber}:${ctx.columnNumber}`;
                    }
                    location = `(${location})`;
                }
                // Format location string
                return `\tat ${ctx.functionName} ${location} - ${ctx.info}`;
            })
                .join('\n');
            outputLines.push(contextInfo);
        }
        return outputLines.join('\n');
    }
}
exports.CodedError = CodedError;
/**
 * Error that is thrown when an unknown error occurs
 *
 * @template Code - The type of the code of the error
 * @template Details - The type of the details object of the error
 */
class UnknownError extends CodedError {
    constructor(message, details) {
        super('UnknownError', message, details);
        this.rawError = details.error;
    }
}
exports.UnknownError = UnknownError;
//# sourceMappingURL=CodedError.js.map